import React, { useEffect } from 'react';
import {
  Content,
  ContentHeader,
  Progress,
  ResponseErrorPanel,
  SupportButton,
  TableColumn,
  TableProps,
} from '@backstage/core-components';
import {
  CatalogFilterLayout,
  DefaultEntityFilters,
  EntityListPagination,
  EntityListProvider,
  EntityOwnerPicker,
  EntityOwnerPickerProps,
  EntityTagPicker,
  useEntityList,
  UserListFilterKind,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  DocsTableRow,
  EntityListDocsTable,
  TechDocsPageWrapper,
  TechDocsPicker,
} from '@backstage/plugin-techdocs';
import {
  AllowedSystemsEntityFilter,
  useAllowedSystemsEntityFilter,
} from '../../hooks/useAllowedSystemsEntityFilter';

type AllowedSystemsTechDocsHomeProps = {
  initialFilter?: UserListFilterKind;
  columns?: TableColumn<DocsTableRow>[];
  actions?: TableProps<DocsTableRow>['actions'];
  ownerPickerMode?: EntityOwnerPickerProps['mode'];
  pagination?: EntityListPagination;
};

type TechDocsEntityFilters = DefaultEntityFilters & {
  allowedSystems?: AllowedSystemsEntityFilter;
};

const AllowedSystemsFilterConfigurator = ({
  filter,
}: {
  filter?: AllowedSystemsEntityFilter;
}) => {
  const { updateFilters } = useEntityList<TechDocsEntityFilters>();

  useEffect(() => {
    updateFilters(() => ({ allowedSystems: filter }));
  }, [filter, updateFilters]);

  return null;
};

export const AllowedSystemsTechDocsHome = (
  props: AllowedSystemsTechDocsHomeProps,
) => {
  const {
    initialFilter = 'owned',
    columns,
    actions,
    ownerPickerMode,
    pagination,
  } = props;

  const {
    filter: allowedSystemsFilter,
    loading: userLoading,
    error: userError,
  } = useAllowedSystemsEntityFilter();

  const renderContent = () => {
    if (userLoading) {
      return <Progress />;
    }

    if (userError) {
      return <ResponseErrorPanel error={userError} />;
    }

    return (
      <EntityListProvider<TechDocsEntityFilters> pagination={pagination}>
        <AllowedSystemsFilterConfigurator filter={allowedSystemsFilter} />
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <TechDocsPicker />
            <UserListPicker initialFilter={initialFilter} />
            <EntityOwnerPicker mode={ownerPickerMode} />
            <EntityTagPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <EntityListDocsTable columns={columns} actions={actions} />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </EntityListProvider>
    );
  };

  return (
    <TechDocsPageWrapper>
      <Content>
        <ContentHeader title="">
          <SupportButton>Discover documentation in your ecosystem.</SupportButton>
        </ContentHeader>
        {renderContent()}
      </Content>
    </TechDocsPageWrapper>
  );
};
